import {Injectable, Injector} from '@angular/core';
import {Observable} from 'rxjs';
import {API_URL, HttpService} from 'projects/core/src/public-api';
import {Task} from './task';
import {ProjectComment} from 'projects/app-misc/src/app/modules/project-misc/data/project/project-comment';
import {ShortTask} from './short-task';
import {ShortObject} from 'projects/app-misc/src/app/modules/project-misc/data/project/short-json-object';
import {ClientNotification} from 'projects/desktop/src/app/view/notification/client-nfs-page/data/client-nfs';
import {FileService} from '../../../../../../../core/src/lib/misc/data/file/file.service';
import {ProjectFile} from 'projects/app-misc/src/app/modules/project-misc/data/project/project-file';
import {HttpEvent} from '@angular/common/http';
import {TaskTabsCount} from './task-tabs-count';


@Injectable({
  providedIn: 'root',
})
export class TasksService extends HttpService {

  public apiUrl: string;

  constructor(
      protected fileService: FileService,
      protected injector: Injector
  ) {
    super(injector);
    this.apiUrl = injector.get(API_URL);
  }

  getTask(taskId: number, clazz: string): Observable<Task> {
    if (clazz === 'SERVICE_ORDER_TASK') {
      return this.getData(`/task/${taskId}/for-service-order`, Task);
    } else if (clazz === 'WORK_ORDER_TASK') {
      return this.getData(`/task/${taskId}/for-work-order`, Task);
    } else {
      return this.getData(`/task/${taskId}`, Task);
    }
  }

  getUnresolvedTasksForProject(projectId: number): Observable<ShortTask[]> {
    return this.getData(`/task/unresolved-service-orders?projectId=${projectId}`, ShortTask);
  }

  addToOrder(taskId: number, checkItemsIds: number[]): Observable<Task> {
    return this.putData(`/task/${taskId}/add-items`, checkItemsIds, Task);
  }

  getShortTask(taskId: number): Observable<ShortTask> {
    return this.getData(`/task/list/${taskId}`, ShortTask);
  }

  getTasksForMe(sortByFieldName1?: string, sortDirection?: string, sortByFieldName2?: string): Observable<ShortTask[]> {
    let url = '/task/list/assigned-to-me';
    const params = [];

    if (sortByFieldName1 && sortDirection) {
      params.push(`sortByFieldName1=${sortByFieldName1}`);
      params.push(`sortDirection=${sortDirection}`);
    }
    if (sortByFieldName2) {
      params.push(`sortByFieldName2=${sortByFieldName2}`);
    }

    if (params.length > 0) {
      url += '?' + params.join('&');
    }

    return this.getData(url, ShortTask);
  }

  getTasksByMe(): Observable<ShortTask[]> {
    return this.getData(`/task/list/created-by-me`, ShortTask);
  }

  getFilteredTasks(usersFrom?: number, usersTo?: number, sortByFieldName1?: string, sortDirection?: string, sortByFieldName2?: string): Observable<ShortTask[]> {
    let url = '/task/list/filtered';
    const params = [];

    if (usersFrom) {
      params.push('usersFrom=' + usersFrom);
    }
    if (usersTo) {
      params.push('usersTo=' + usersTo);
    }
    if (sortByFieldName1 && sortDirection) {
      params.push(`sortByFieldName1=${sortByFieldName1}`);
      params.push(`sortDirection=${sortDirection}`);
    }
    if (sortByFieldName2) {
      params.push(`sortByFieldName2=${sortByFieldName2}`);
    }

    if (params.length > 0) {
      url += '?' + params.join('&');
    }

    return this.getData(url, ShortTask);
  }

  getTasksByEntityId(clazz: string, entityId: number): Observable<Task[]> {
    return this.getData(`/task/list/for-entity?entityId=${entityId}&clazz=${clazz}`, Task);
  }

  getTaskForProject(projectId: number): Observable<Task[]> {
    return this.getData(`/task/list/for-project?projectId=${projectId}`, Task);
  }

  getTaskComments(taskId: number): Observable<ProjectComment[]> {
    return this.getData(`/task/${taskId}/comments`, ProjectComment);
  }

  getConditionTypes(): Observable<ShortObject[]> {
    return this.getData(`/notification-condition/types/for-service-orders`, ShortObject);
  }

  createMessage(orderId: number, condId: number): Observable<ClientNotification> {
    return this.postData(`/notification/list/for-clients/for-service-order/${orderId}?conditionId=${condId}`, {}, ClientNotification)
  }

  addTaskComment(taskId: number, comment: ProjectComment): Observable<ProjectComment> {
    return this.postData(`/task/${taskId}/comments`, comment, ProjectComment);
  }

  updateTaskStatus(taskId: number, status: boolean): Observable<Task> {
    return this.putData(`/task/${taskId}/complete?value=${status}`, {}, Task);
  }

  updateTask(task: Task, workOrder: boolean = false): Observable<Task> {
    if (workOrder) {
      return this.putData(`/task/for-service-order`, task, Task, true);
    } else {
      return this.putData(`/task`, task, Task, true);
    }
  }

  markTaskAsRead(taskId: number): Observable<Task> {
    return this.putData(`/task/${taskId}/mark-as-read`, {}, Task);
  }

  createTask(task: Task): Observable<Task> {
    return this.postData(`/task`, task, Task, true);
  }

  createTaskFromCheckItem(task: Task): Observable<Task> {
    return this.postData(`/task/for-checkitem`, task, Task, true);
  }

  createTaskFromComment(task: Task): Observable<Task> {
    return this.postData(`/task/for-comment`, task, Task, true);
  }

  createWorkOrder(task: Task): Observable<Task> {
    return this.postData(`/task/for-service-order`, task, Task, true);
  }

  updateWorkOrder(task: Task): Observable<Task> {
    return this.putData(`/task/for-service-order`, task, Task, true);
  }

  getFiles(taskId: number): Observable<ProjectFile[]> {
    return this.getData(this.fileService.getListFilesLink('task', taskId), ProjectFile);
  }

  uploadFile(files: File[], taskId: number): Observable<HttpEvent<ProjectFile[]>> {
    return this.fileService.postFile<ProjectFile[]>(this.fileService.getUploadFileLink('task', taskId), 'files', files);
  }

  deleteFile(fileId: number, taskId: number): Observable<ProjectFile> {
    return this.deleteData(this.fileService.getDeleteFileLink(taskId), ProjectFile)
  }

  deleteTask(taskId: number): Observable<Task> {
    return this.deleteData(`/task/${taskId}`, Task)
  }

  getProjectTabsRegular(projectId: number): Observable<Task[]> {
    return this.getData(`/task/project-tabs/regular?projectId=${projectId}`, Task);
  }

  getProjectTabsWorkOrder(projectId: number): Observable<Task[]> {
    return this.getData(`/task/project-tabs/work-order?projectId=${projectId}`, Task);
  }

  getProjectTabsCheckItem(projectId: number): Observable<Task[]> {
    return this.getData(`/task/project-tabs/check-item?projectId=${projectId}`, Task);
  }

  getProjectTabsCount(projectId: number): Observable<TaskTabsCount> {
    return this.getData(`/task/project-tabs/count?projectId=${projectId}`, TaskTabsCount);
  }

}
